import { useState, useCallback } from 'react';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconImageOutline } from '@picsart/design-system/foundation/icons/IconImageOutline';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { IconSearch } from '@picsart/design-system/foundation/icons/IconSearch';
import { useClickOutsideRef } from '@picsart/design-system/hooks/dom';
import { useDeviceType } from '@picsart/design-system/hooks/device';
import UserMenuButton from '@/components/UserMenuButton';
import SearchDropdown from '@/components/SearchDropdown';
import StreakPill from '@/components/StreakPill';
import GenerationStatusBadge from '@/components/GenerationStatusBadge';
import SettingsPanel from '@/components/SettingsPanel';
import { SettingsPanelVariant } from '@/components/SettingsPanel/types';
import useUserProfile from '@/hooks/useUserProfile';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import useStyles from './styles';
import type { HeaderRightProps } from './types';

const HeaderRight = ({
  showGenerationsButton,
  onGenerationsClick,
  showSearch,
  onSearchClick,
  onCloseApp,
  onSettingsClick,
  onAvatarClick,
}: HeaderRightProps) => {
  const classes = useStyles();
  const isPortfolioApp = checkIsPortfolioApp();
  const {
    isAuthorized: userIsAuthorized,
    username,
    avatarSrc,
    tierId,
  } = useUserProfile();
  const { isMobile } = useDeviceType();
  const [showDesktopSettings, setShowDesktopSettings] = useState(false);
  const closeSettings = useCallback(() => setShowDesktopSettings(false), []);
  const settingsRef = useClickOutsideRef(closeSettings, [], false);

  const handleSettingsClick = () => {
    if (isMobile) {
      onSettingsClick?.();
    } else {
      setShowDesktopSettings((v) => !v);
    }
  };

  return (
    <div className={classes.right}>
      {/* Search. Mobile is an icon that opens the full-screen SearchScreen
          (mounted by AppShell, tracked by `isSearchOpen`); desktop/tablet has
          a permanent field with a suggestion popover, which needs no flag.
          Hidden off the preset-browsing screens — see `showSearch`. */}
      {showSearch &&
        (isMobile ? (
          <InlineButton
            size="large"
            centerIcon={IconSearch}
            className={classes.iconButton}
            isWithoutBorder
            isWithoutBackground
            onClick={onSearchClick}
            aria-label="Search"
            title="Search"
            dataTestId="header-right-search"
          />
        ) : (
          <SearchDropdown />
        ))}
      {/* Generations button - CSS hides on desktop; hidden entirely on the
          portfolio surface (creations are reachable from the profile screen) */}
      {showGenerationsButton && !isPortfolioApp && (
        <span className={classes.generationsButton}>
          <InlineButton
            size="large"
            centerIcon={IconImageOutline}
            className={classes.iconButton}
            isWithoutBorder
            isWithoutBackground
            onClick={onGenerationsClick}
            aria-label="Your Creations"
            title="Your Creations"
            dataTestId="header-right-generations"
          />
          <GenerationStatusBadge />
        </span>
      )}
      {isPortfolioApp ? (
        <div className={classes.settingsWrapper}>
          <StreakPill />
          <span className={classes.avatarButton}>
            <UserMenuButton
              isAuthorized={userIsAuthorized}
              username={username}
              avatarSrc={avatarSrc}
              tierId={tierId}
              showUserMenu
              onCloseApp={onCloseApp}
              onSettingsClick={handleSettingsClick}
              onAvatarClick={onAvatarClick}
            />
            <GenerationStatusBadge />
          </span>
          {showDesktopSettings && !isMobile && (
            <div ref={settingsRef}>
              <SettingsPanel
                variant={SettingsPanelVariant.Dropdown}
                isAuthorized={userIsAuthorized}
              />
            </div>
          )}
        </div>
      ) : (
        <InlineButton
          size="large"
          centerIcon={IconCross}
          className={classes.iconButton}
          isWithoutBorder
          isWithoutBackground
          onClick={onCloseApp}
          aria-label="Close app"
          title="Close"
          dataTestId="header-right-close"
        />
      )}
    </div>
  );
};

export default HeaderRight;
